import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import adminApi from '../api/client';

interface AuthContextType {
  adminName: string;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (name: string) => void;
  logout: () => Promise<void>;
  logoutError?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // isLoading: true while the initial /admin/me check is in flight.
  // ProtectedRoute must not render a redirect until this resolves, otherwise
  // a page refresh always flashes a redirect-to-login before the cookie is verified.
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [adminName, setAdminName] = useState<string>('Admin');
  const [logoutError, setLogoutError] = useState('');

  // On mount: call GET /admin/me to rehydrate auth state from the httpOnly
  // cookie. The browser sends the cookie automatically (withCredentials=true).
  useEffect(() => {
    adminApi
      .get<{ admin_name: string; email: string }>('/admin/me')
      .then((res) => {
        setIsAuthenticated(true);
        setAdminName(res.data.admin_name);
      })
      .catch(() => {
        // 401 or network error — not authenticated. Don't redirect here;
        // ProtectedRoute handles the redirect once isLoading is false.
        setIsAuthenticated(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  // Called immediately after a successful /admin/verify-2fa response.
  // The cookie is already set by the backend's Set-Cookie header — the browser
  // handles that automatically. We just capture admin_name for display.
  const login = useCallback((name: string) => {
    setIsAuthenticated(true);
    setAdminName(name);
  }, []);

  // Calls POST /admin/logout first so the backend clears the cookie (sends
  // Set-Cookie with past expiry), then resets local state and redirects.
  const logout = useCallback(async () => {
    setLogoutError('');
    try {
      await adminApi.post('/admin/logout');
    } catch {
      setLogoutError('Logout could not be confirmed. Please retry before leaving this computer.');
      return;
    }
    setIsAuthenticated(false);
    setAdminName('Admin');
    window.location.href = '/login';
  }, []);

  return (
    <AuthContext.Provider value={{ adminName, isAuthenticated, isLoading, login, logout, logoutError }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
